import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Pause, Play } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Badge, Dialog, Field, PageHeader, Skeleton, Spinner, Table, Td, Th, useConfirm, useToast } from '../../components/ui';
import { api } from '../../lib/api';
import { statusTone, type TenantRow } from './TenantsPage';

interface Sub { id: string; status: string; startsAt: string; endsAt: string | null; trialEndsAt: string | null; vehicleLimitOverride: number | null; plan: { code: string; nameAr: string; nameEn: string; maxVehicles: number } }
interface TenantDetail extends Omit<TenantRow, 'subscription'> { timezone: string; locale: string; subscriptions: Sub[]; _count: TenantRow['_count'] & { drivers: number } }
interface Plan { code: string; nameAr: string; nameEn: string; maxVehicles: number; priceMonthlyEgp: string }

const fmt = (s: string | null, lang: string) => (s ? new Intl.DateTimeFormat(lang === 'en' ? 'en-EG' : 'ar-EG-u-nu-latn', { dateStyle: 'medium' }).format(new Date(s)) : '—');

export function TenantDetailPage() {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const confirmDialog = useConfirm();
  const qc = useQueryClient();
  const [changePlan, setChangePlan] = useState(false);
  const q = useQuery({ queryKey: ['super-tenant', id], queryFn: () => api<TenantDetail>(`/super/tenants/${id}`) });
  const name = (r: { nameAr: string; nameEn: string | null }) => (i18n.language === 'en' && r.nameEn ? r.nameEn : r.nameAr);
  const setStatus = useMutation({
    mutationFn: (action: 'suspend' | 'activate') => api(`/super/tenants/${id}/${action}`, { method: 'POST' }),
    onSuccess: () => { toast.success(t('common.saved')); void qc.invalidateQueries({ queryKey: ['super-tenant', id] }); void qc.invalidateQueries({ queryKey: ['super-tenants'] }); },
    onError: toast.error,
  });

  if (q.isLoading || !q.data) return <Skeleton className="h-96" />;
  const tn = q.data;
  const active = tn.subscriptions.find((s) => s.status === 'ACTIVE' || s.status === 'TRIAL');

  return (
    <div className="flex flex-col gap-5">
      <Link to="/admin/tenants" className="flex items-center gap-1 text-sm text-text-muted hover:text-text"><ArrowRight size={14} className="rtl:rotate-0 ltr:rotate-180" /> {t('admin.tenants')}</Link>
      <PageHeader
        title={name(tn)}
        subtitle={`${tn.slug} · ${tn.timezone}`}
        actions={
          <div className="flex gap-2">
            <button className="btn-secondary" onClick={() => setChangePlan(true)}>{t('admin.changePlan')}</button>
            {tn.status === 'SUSPENDED' ? (
              <button className="btn-primary" onClick={() => setStatus.mutate('activate')} disabled={setStatus.isPending}><Play size={16} /> {t('admin.activate')}</button>
            ) : (
              <button className="btn-danger" onClick={() => confirmDialog(t('admin.suspendConfirm'), { danger: true }).then((ok) => ok && setStatus.mutate('suspend'))} disabled={setStatus.isPending}><Pause size={16} /> {t('admin.suspend')}</button>
            )}
          </div>
        }
      />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Stat label={t('admin.status')}><Badge tone={statusTone(tn.status)} dot>{t(`admin.statuses.${tn.status}`)}</Badge></Stat>
        <Stat label={t('admin.plan')}>{active ? name(active.plan) : '—'}</Stat>
        <Stat label={t('nav.vehicles')}>{tn._count.vehicles}{active ? <span className="text-sm text-text-faint"> / {active.vehicleLimitOverride ?? active.plan.maxVehicles}</span> : null}</Stat>
        <Stat label={t('nav.drivers')}>{tn._count.drivers}</Stat>
        <Stat label={t('admin.devices')}>{tn._count.devices}</Stat>
      </div>
      <h3 className="text-sm font-bold">{t('admin.subscriptions')}</h3>
      <Table>
        <thead><tr><Th>{t('admin.plan')}</Th><Th>{t('admin.status')}</Th><Th>{t('admin.startsAt')}</Th><Th>{t('admin.trialEndsAt')}</Th><Th>{t('admin.endsAt')}</Th><Th>{t('admin.vehicleLimit')}</Th></tr></thead>
        <tbody>
          {tn.subscriptions.map((s) => (
            <tr key={s.id}><Td className="font-semibold">{name(s.plan)}</Td><Td><Badge tone={s.status === 'ACTIVE' ? 'success' : s.status === 'TRIAL' ? 'info' : 'neutral'}>{s.status}</Badge></Td><Td>{fmt(s.startsAt, i18n.language)}</Td><Td>{fmt(s.trialEndsAt, i18n.language)}</Td><Td>{fmt(s.endsAt, i18n.language)}</Td><Td>{s.vehicleLimitOverride ?? s.plan.maxVehicles}</Td></tr>
          ))}
        </tbody>
      </Table>
      {changePlan ? <ChangePlanDialog tenantId={id} onClose={() => setChangePlan(false)} /> : null}
    </div>
  );
}

function Stat({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="card p-4"><div className="text-xs text-text-muted">{label}</div><div className="mt-1 text-xl font-bold">{children}</div></div>;
}

function ChangePlanDialog({ tenantId, onClose }: { tenantId: string; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const plans = useQuery({ queryKey: ['super-plans'], queryFn: () => api<Plan[]>('/super/plans') });
  const [v, setV] = useState({ planCode: '', status: 'ACTIVE', vehicleLimitOverride: '' });
  const save = useMutation({
    mutationFn: () => api(`/super/tenants/${tenantId}/subscriptions`, { method: 'POST', json: { planCode: v.planCode, status: v.status, vehicleLimitOverride: v.vehicleLimitOverride ? Number(v.vehicleLimitOverride) : null } }),
    onSuccess: () => { toast.success(t('common.saved')); void qc.invalidateQueries({ queryKey: ['super-tenant', tenantId] }); onClose(); },
    onError: toast.error,
  });
  return (
    <Dialog open onClose={onClose} title={t('admin.changePlan')} footer={<><button className="btn-secondary" onClick={onClose}>{t('common.cancel')}</button><button className="btn-primary" disabled={!v.planCode || save.isPending} onClick={() => save.mutate()}>{save.isPending ? <Spinner /> : null}{t('common.save')}</button></>}>
      <div className="grid gap-4">
        <Field label={t('admin.plan')} required>{(id) => <select id={id} className="input" value={v.planCode} onChange={(e) => setV({ ...v, planCode: e.target.value })}><option value="">—</option>{plans.data?.map((p) => <option key={p.code} value={p.code}>{i18n.language === 'en' ? p.nameEn : p.nameAr} — {p.maxVehicles}</option>)}</select>}</Field>
        <Field label={t('admin.status')}>{(id) => <select id={id} className="input" value={v.status} onChange={(e) => setV({ ...v, status: e.target.value })}><option value="ACTIVE">ACTIVE</option><option value="TRIAL">TRIAL</option></select>}</Field>
        <Field label={t('admin.vehicleLimit')} hint={t('admin.vehicleLimitHint')}>{(id) => <input id={id} type="number" dir="ltr" className="input" value={v.vehicleLimitOverride} onChange={(e) => setV({ ...v, vehicleLimitOverride: e.target.value })} />}</Field>
      </div>
    </Dialog>
  );
}
